import { api } from './api';

/**
 * Interface de um item na listagem de documentos (espelhando backend DocumentoListItem).
 */
export interface DocumentoListItem {
  id: string;
  nomeOriginal: string;
  descricao: string;
  formato: string;
  tamanhoByte: number;
  enviadoEm: string;
  enviadoPor: { id: string; nome: string };
}

/**
 * Resultado do upload em lote.
 */
export interface UploadResult {
  sucesso: unknown[];
  erros: { nomeArquivo: string; erro: string }[];
}

/**
 * Lista documentos não excluídos de um empreendimento.
 * GET /api/empreendimentos/:empreendimentoId/documentos?busca=...
 */
export async function listarDocumentos(empreendimentoId: string, busca?: string): Promise<DocumentoListItem[]> {
  const params = busca ? `?busca=${encodeURIComponent(busca)}` : '';
  return api.get(`/empreendimentos/${empreendimentoId}/documentos${params}`);
}

/**
 * Upload de múltiplos documentos para um empreendimento.
 * POST /api/empreendimentos/:empreendimentoId/documentos (multipart/form-data)
 */
export async function uploadDocumentos(
  empreendimentoId: string,
  arquivos: File[],
  descricoes: string[],
): Promise<UploadResult> {
  const formData = new FormData();
  arquivos.forEach((file) => formData.append('arquivos', file));
  formData.append('descricoes', JSON.stringify(descricoes));
  return api.post(`/empreendimentos/${empreendimentoId}/documentos`, formData);
}

/**
 * Inicia o download de um documento preservando o nome original.
 * GET /api/empreendimentos/:empreendimentoId/documentos/:id/download
 */
export function downloadDocumento(empreendimentoId: string, documentoId: string): void {
  const token = localStorage.getItem('auth_token');
  const url = `/api/empreendimentos/${empreendimentoId}/documentos/${documentoId}/download`;

  fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  })
    .then((res) => {
      if (!res.ok) throw new Error('Erro ao baixar documento.');
      return res.blob();
    })
    .then((blob) => {
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = blobUrl;
      a.download = '';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(blobUrl);
    })
    .catch(() => {
      // Silently fail — caller can handle errors
    });
}

/**
 * Exclusão lógica de um documento.
 * DELETE /api/empreendimentos/:empreendimentoId/documentos/:id
 */
export async function excluirDocumento(empreendimentoId: string, documentoId: string): Promise<void> {
  await api.delete(`/empreendimentos/${empreendimentoId}/documentos/${documentoId}`);
}

/**
 * Atualiza a descrição de um documento.
 * PATCH /api/empreendimentos/:empreendimentoId/documentos/:id
 */
export async function editarDescricaoDocumento(
  empreendimentoId: string,
  documentoId: string,
  descricao: string,
): Promise<void> {
  await api.patch(`/empreendimentos/${empreendimentoId}/documentos/${documentoId}`, { descricao });
}

/**
 * Retorna a URL de download de um documento.
 */
export function getDownloadUrl(empreendimentoId: string, documentoId: string): string {
  return `/api/empreendimentos/${empreendimentoId}/documentos/${documentoId}/download`;
}

/**
 * Formatos que suportam visualização inline no navegador.
 */
const INLINE_FORMATS = ['jpg', 'png', 'pdf'];

/**
 * Verifica se o formato suporta visualização inline (nova aba).
 */
export function isInlineFormat(formato: string): boolean {
  return INLINE_FORMATS.includes(formato.toLowerCase());
}

/**
 * Abre o documento em nova aba (para formatos inline) ou inicia download.
 * Usa token de autenticação via fetch + blob para garantir acesso autenticado.
 */
export async function abrirOuBaixarDocumento(
  empreendimentoId: string,
  documentoId: string,
  formato: string,
  nomeOriginal: string,
): Promise<void> {
  const token = localStorage.getItem('auth_token');
  const url = getDownloadUrl(empreendimentoId, documentoId);

  const res = await fetch(url, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });

  if (!res.ok) {
    throw new Error('Não foi possível recuperar o arquivo.');
  }

  const blob = await res.blob();
  const blobUrl = URL.createObjectURL(blob);

  if (isInlineFormat(formato)) {
    // Abrir em nova aba para visualização inline
    window.open(blobUrl, '_blank');
  } else {
    // Iniciar download com nome original
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = nomeOriginal;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(blobUrl);
  }
}
